import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SettingsOutlined } from '@mui/icons-material';
import {
  PRIMARY, LOBBY_BREAKPOINTS,
  resolveHotspotForBp, isHotspotVisibleOnBp,
} from '../constants';
import { LobbyHeader } from '../LobbyHeader';
import { CanvasBackground } from './CanvasBackground';
import { HotspotSVG }       from './HotspotSVG';
import { SelectionGizmo }   from './SelectionGizmo';
import { PenToolSVGLayer as PenToolOverlay, PathEditSVGLayer as PathEditOverlay } from './PenTool';
import { CanvasToolbar }    from './CanvasToolbar';

// Split modules
import { ExhibitorGridElement, TextElement, ImageElement, StreamElement } from './CanvasElements';
import { LobbyWizard } from './LobbyWizard';
import {
  CANVAS_HOTSPOT, CanvasHotspotHitRect, DrawPreview,
  ElementContextMenu, ThumbPickerInline,
} from './CanvasHelpers';
import { ImagePopover } from './CanvasPopovers';
export { HotspotStripContents } from './HotspotStrip';

export function LobbyCanvas({
  canvasRef,
  lobby,
  hotspots,
  selectedId,
  canvasSelected,
  previewMode,
  ghost,
  dragTypeRef,
  onCanvasClick,
  onCanvasDragOver,
  onCanvasDrop,
  onHotspotMouseDown,
  onHotspotClick,
  onHandleMouseDown,
  onGizmoBodyMouseDown,
  activeTool,
  onSelectTool,
  penTool,
  pathEdit,
  drawState,
  onDrawMouseDown,
  onDrawMouseMove,
  onDrawMouseUp,
  onOpenChatModal,
  onHotspotUpdate,
  onUpdateLobby,
  onHotspotPreviewClick,
  lobbies = [],
  lobbyHeaderConfig,
  headerSelected = false,
  onSelectHeader,
  onChangeLinks,
  onChangeConfig,
  exhibitors = [],
  selectedLobbyExhibitorId = null,
  onSelectLobbyExhibitor,
  onAddExhibitor,
  onDeleteExhibitor,
  onReorderExhibitors,
  onCreateExhibitorLobby,
  onGoToExhibitorLobby,
  onUpdateExhibitor,
  onAddSlideForExhibitor,
  activeSlideIndex = 0,
  onBackToFirstSlide,
  zoom = 1,
  breakpoint,
  // ── NEW: create first lobby from wizard when no lobby exists yet ──
  onCreateFirstLobby,
  onDeleteHotspot,
  defaultLogoSrc = '',
  overlayContent = null,
  // slideTrack — when set, replaces the static CanvasBackground with a
  // two-panel sliding track for the carousel transition animation.
  // Shape: { currentBgUrl, currentBgType, incomingBgUrl, incomingBgType, direction, phase }
  slideTrack = null,
  // incomingHotspots — hotspots for the incoming slide during a carousel transition
  incomingHotspots = null,
}) {
  const selectedHotspot = hotspots.find(h => h.id === selectedId) || null;
  const isPen         = activeTool === 'pen'    && !previewMode;
  const isRect        = activeTool === 'rect'   && !previewMode;
  const isCircle      = activeTool === 'circle' && !previewMode;
  const isShapeDraw   = isRect || isCircle;

  // ── Breakpoint-aware canvas dimensions ──────────────────────────────────
  const bp = breakpoint || 'desktop';
  const bpSpec = LOBBY_BREAKPOINTS.find(b => b.key === bp) || LOBBY_BREAKPOINTS[0];
  const bpCanvasW = bpSpec.canvasW;  // used for aspect ratio
  const bpCanvasH = bpSpec.canvasH;
  const bpAspect  = bpCanvasH / bpCanvasW;

  // Effective bg — use responsive override for non-desktop breakpoints
  const bpBg = !bp || bp === 'desktop' ? {} : (lobby?.responsive?.[bp] || {});
  const effectiveBgUrl  = bpBg.bgUrl  !== undefined ? bpBg.bgUrl  : (lobby?.bgUrl  || null);
  const effectiveBgType = bpBg.bgType !== undefined ? bpBg.bgType : (lobby?.bgType || 'image');
  const isEditingPath = !!pathEdit?.hotspot && !previewMode;

  const [canvasScale, setCanvasScale] = useState(1);
  const [imgPopover, setImgPopover] = useState(null); // { hotspot, elemRef }
  const [ctxMenu,    setCtxMenu]    = useState(null); // { x, y, hotspotId }
  useEffect(() => {
    if (!canvasRef?.current) return;
    const compute = () => { if (canvasRef.current) setCanvasScale(canvasRef.current.clientWidth / bpCanvasW); };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [canvasRef]);

  const chatHotspots    = hotspots.filter(h => h.hotspotType === 'chat');
  const hasChatHotspots = chatHotspots.length > 0;

  let cursor = 'default';
  if (isPen || isShapeDraw) cursor = 'crosshair';
  else if (dragTypeRef.current) cursor = 'crosshair';

  const handleHotspotDoubleClick = (e, h) => {
    e.stopPropagation();
    if (h.hotspotType === 'chat') {
      onOpenChatModal?.(h.id);
    } else if (h.meta?.isBezier && !isEditingPath) {
      pathEdit?.onEnter(h);
    }
  };

  const openCtxMenu = (e, hotspotId) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, hotspotId });
  };

  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef({ dragging: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
  const outerRef = useRef(null);

  useEffect(() => { if (zoom <= 1) setPan({ x: 0, y: 0 }); }, [zoom]);

  const handleMiddleMouseDown = useCallback((e) => {
    if (e.button !== 1) return;
    e.preventDefault();
    panRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
    const onMove = (ev) => {
      if (!panRef.current.dragging) return;
      setPan({ x: panRef.current.startPanX + (ev.clientX - panRef.current.startX), y: panRef.current.startPanY + (ev.clientY - panRef.current.startY) });
    };
    const onUp = () => { panRef.current.dragging = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pan]);

  // Show wizard only when there's genuinely no background AND no hotspots.
  // On non-desktop breakpoints we always show the canvas (user is overriding an
  // existing lobby — they don't need the wizard again even if bp bg is empty).
  // For carousel lobbies the wizard is never shown — each slide's bg is set via
  // the inspector, not the wizard, and we need the canvas to always be clickable
  // so the user can open the inspector and navigate slides.
  const isCarousel = lobby?.lobbyType === 'carousel';
  const showWizard         = !isCarousel && !lobby?.bgUrl && hotspots.length === 0 && !previewMode && bp === 'desktop';
  // For carousel: show wizard inside the slide area when the active slide has no background.
  // Unlike the regular wizard it does NOT cover the header/arrows (those render above it).
  const showCarouselWizard = isCarousel && !lobby?.bgUrl && !previewMode && hotspots.length === 0;
  // If no lobby object at all, route background selection through onCreateFirstLobby
  const wizardCreateHandler = !lobby ? onCreateFirstLobby : undefined;

  return (
    <Box
      ref={outerRef}
      onMouseDown={handleMiddleMouseDown}
      sx={{
        flex: 1, overflow: 'auto', p: 3,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        cursor: panRef.current?.dragging ? 'grabbing' : 'default',
        '&::-webkit-scrollbar': { width: 6, height: 6 },
        '&::-webkit-scrollbar-thumb': { background: '#c4cdd8', borderRadius: 3 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
      }}
    >
      <Box sx={{
        // Desktop: fill available width up to the aspect-ratio-constrained max.
        // Tablet/Mobile: use a fixed pixel width (the simulated device width) capped
        // by the available height so it never overflows the viewport vertically.
        // paddingBottom % trick only works when the element has a real width,
        // so we must NOT use 'auto' here.
        width: bp === 'desktop' ? '100%' : `min(${bpCanvasW}px, calc((100vh - 60px - 48px - 120px - 48px) / ${bpAspect}))`,
        maxWidth: bp === 'desktop'
          ? `calc((100vh - 60px - 48px - 120px - 48px) / ${bpAspect})`
          : undefined,
        flexShrink: 0,
        transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
        transformOrigin: 'center center',
        transition: panRef.current?.dragging ? 'none' : 'transform 0.15s ease',
      }}>
        <Box sx={{ position: 'relative', width: '100%', paddingBottom: `${bpAspect * 100}%` }}>

          {/* ── Canvas ────────────────────────────────────────────────────── */}
          <Box
            ref={canvasRef}
            data-canvas-box
            onDragOver={!isPen && !isShapeDraw ? onCanvasDragOver : undefined}
            onDrop={!isPen && !isShapeDraw ? onCanvasDrop : undefined}
            onClick={(!isCarousel && showWizard) ? undefined : (!isPen && !isShapeDraw && !isEditingPath ? onCanvasClick : undefined)}
            sx={{
              position: 'absolute', inset: 0,
              overflow: 'hidden', borderRadius: 2,
              boxShadow: canvasSelected && !previewMode
                ? `0 0 0 3px ${PRIMARY}, 0 8px 40px rgba(0,0,0,0.25)`
                : '0 8px 40px rgba(0,0,0,0.25)',
              cursor, transition: 'box-shadow 0.15s',
            }}
          >
            {/* Background — static when no transition, sliding track during carousel nav */}
            {slideTrack ? (() => {
              // Build a 200%-wide track containing [current | incoming] or [incoming | current]
              // depending on direction, then translate it so the correct panel fills the canvas.
              const isRight   = slideTrack.direction === 'right';
              // At rest: current fills the viewport (track at 0 or -50%)
              // Animating: translate so incoming fills the viewport
              const restX     = isRight ? '0%'   : '-50%';   // initial position
              const animateX  = isRight ? '-50%'  : '0%';    // animated-to position
              const translateX = slideTrack.phase === 'animate' ? animateX : restX;

              const makeBg = (bgUrl, bgType) => bgUrl
                ? bgType === 'video'
                  ? <video key={bgUrl} autoPlay muted loop playsInline src={bgUrl}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                  : <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                : <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1a2540 0%, #2d4a6b 50%, #1a3050 100%)' }} />;

              // Left panel and right panel of the track
              const leftBg  = isRight ? { url: slideTrack.currentBgUrl,  type: slideTrack.currentBgType  }
                                       : { url: slideTrack.incomingBgUrl, type: slideTrack.incomingBgType };
              const rightBg = isRight ? { url: slideTrack.incomingBgUrl, type: slideTrack.incomingBgType }
                                       : { url: slideTrack.currentBgUrl,  type: slideTrack.currentBgType  };

              return (
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, bottom: 0,
                  width: '200%',
                  transform: `translateX(${translateX})`,
                  transition: slideTrack.phase === 'animate'
                    ? 'transform 0.38s cubic-bezier(0.4, 0, 0.2, 1)'
                    : 'none',
                  display: 'flex',
                  pointerEvents: 'none',
                }}>
                  {/* Left half */}
                  <Box sx={{ position: 'relative', width: '50%', height: '100%', flexShrink: 0, overflow: 'hidden' }}>
                    {makeBg(leftBg.url, leftBg.type)}
                  </Box>
                  {/* Right half */}
                  <Box sx={{ position: 'relative', width: '50%', height: '100%', flexShrink: 0, overflow: 'hidden' }}>
                    {makeBg(rightBg.url, rightBg.type)}
                  </Box>
                </Box>
              );
            })() : (
              <CanvasBackground lobby={lobby ? { ...lobby, bgUrl: effectiveBgUrl, bgType: effectiveBgType } : lobby} />
            )}

            {/* Header inside canvas — only for non-carousel (carousel header rendered below, outside canvasRef) */}
            {!isCarousel && lobbyHeaderConfig && lobby && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
                <LobbyHeader
                  config={lobbyHeaderConfig}
                  selected={headerSelected}
                  onSelect={onSelectHeader}
                  onChangeLinks={onChangeLinks}
                  onChangeConfig={onChangeConfig}
                  lobbies={lobbies}
                  isBoothLobby={lobby?.lobbyType === 'booth'}
                  activeLobby={lobby}
                  onChangeLobby={onUpdateLobby}
                  isMobile={bp === 'mobile'}
                  defaultLogoSrc={defaultLogoSrc}
                />
              </Box>
            )}

            {/* ── Hotspot content — wrapped in sliding track when carousel transition is active ── */}
            {(() => {
              // Build the shared track transform (mirrors the bg track exactly)
              const trackStyle = slideTrack ? (() => {
                const isRight  = slideTrack.direction === 'right';
                const restX    = isRight ? '0%'  : '-50%';
                const animateX = isRight ? '-50%' : '0%';
                return {
                  position: 'absolute', top: 0, left: 0, bottom: 0, width: '200%',
                  transform: `translateX(${slideTrack.phase === 'animate' ? animateX : restX})`,
                  transition: slideTrack.phase === 'animate' ? 'transform 0.38s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                  display: 'flex', pointerEvents: 'none',
                };
              })() : null;

              // Panel that holds hotspot content for ONE slide worth of hotspots.
              // `hs` is the hotspot array; `interactive` controls pointer events.
              // `panelShowWizard` — true when this slide has no bg and the wizard should appear.
              const renderPanel = (hs, interactive, panelShowWizard = false) => (
                <Box sx={{ position: 'relative', width: slideTrack ? '50%' : '100%', height: '100%', flexShrink: 0 }}>
                  {/* absolute fill so coordinate % math works identically to the normal canvas */}
                  <Box sx={{ position: 'absolute', inset: 0, pointerEvents: interactive ? 'all' : 'none' }}>

                    {[...hs.filter(h => h.hotspotType === 'stream' || h.hotspotType === 'livestream-chat' || h.hotspotType === 'exhibitor-grid')].sort((a,b) => (a.order??0)-(b.order??0)).map(h => {
                      const hResolved = resolveHotspotForBp(h, bp);
                      const isVisible = isHotspotVisibleOnBp(h, bp);
                      const elem = h.hotspotType === 'exhibitor-grid' ? (
                        <ExhibitorGridElement
                          key={h.id}
                          hotspot={hResolved}
                          exhibitors={exhibitors}
                          selected={interactive && selectedId === h.id}
                          selectedLobbyExhibitorId={interactive ? selectedLobbyExhibitorId : null}
                          onSelectLobbyExhibitor={interactive ? onSelectLobbyExhibitor : undefined}
                          onSelectGrid={() => interactive && !previewMode && onHotspotClick(h.id)}
                          onAddExhibitor={interactive ? onAddExhibitor : undefined}
                          onDeleteExhibitor={interactive ? onDeleteExhibitor : undefined}
                          onReorderExhibitors={interactive ? onReorderExhibitors : undefined}
                          onCreateExhibitorLobby={interactive ? onCreateExhibitorLobby : undefined}
                          onGoToExhibitorLobby={interactive ? onGoToExhibitorLobby : undefined}
                          onUpdateExhibitor={interactive ? onUpdateExhibitor : undefined}
                          onAddSlideForExhibitor={interactive ? onAddSlideForExhibitor : undefined}
                          canvasScale={canvasScale}
                          bp={bp}
                          lobbies={lobbies}
                          lobbySlides={lobby?.slides || []}
                          isCarouselLobby={lobby?.lobbyType === 'carousel'}
                          onMouseDown={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotMouseDown(e, h.id); } : undefined}
                          onClick={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotClick(h.id); } : undefined}
                        />
                      ) : (
                        <StreamElement
                          key={h.id}
                          hotspot={hResolved}
                          canvasScale={canvasScale}
                          selected={interactive && selectedId === h.id}
                          onMouseDown={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotMouseDown(e, h.id); } : undefined}
                          onClick={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotClick(h.id); } : undefined}
                          onHandleMouseDown={interactive && !previewMode ? onHandleMouseDown : undefined}
                          onHotspotUpdate={interactive ? onHotspotUpdate : undefined}
                        />
                      );
                      if (!previewMode && !isVisible) {
                        return (
                          <Box key={h.id} sx={{ position: 'absolute', inset: 0, opacity: interactive ? 0.3 : 0.15, pointerEvents: interactive ? 'all' : 'none' }}>
                            {elem}
                          </Box>
                        );
                      }
                      return elem;
                    })}

                    {[...hs.filter(h => h.hotspotType === 'text')].sort((a,b) => (a.order??0)-(b.order??0)).map(h => {
                      const hResolved = resolveHotspotForBp(h, bp);
                      const isVisible = isHotspotVisibleOnBp(h, bp);
                      const elem = (
                        <TextElement
                          key={h.id}
                          hotspot={hResolved}
                          bp={bp}
                          selected={interactive && selectedId === h.id}
                          previewMode={previewMode || !interactive}
                          canvasScale={canvasScale}
                          onMouseDown={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotMouseDown(e, h.id); } : undefined}
                          onClick={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotClick(h.id); } : undefined}
                          onUpdate={interactive ? onHotspotUpdate : undefined}
                          onHandleMouseDown={interactive && !previewMode ? (e, handle) => onHandleMouseDown(e, handle) : undefined}
                          onContextMenu={interactive && !previewMode ? e => openCtxMenu(e, h.id) : undefined}
                        />
                      );
                      if (!previewMode && !isVisible) {
                        return (
                          <Box key={h.id} sx={{ position: 'absolute', inset: 0, pointerEvents: interactive ? 'all' : 'none' }}>
                            <Box sx={{ opacity: 0.25, filter: 'grayscale(0.6)', pointerEvents: 'all' }}>{elem}</Box>
                            <Box sx={{
                              position: 'absolute',
                              left: `${hResolved.points?.[0]?.x ?? 0}%`, top: `${hResolved.points?.[0]?.y ?? 0}%`,
                              width: `${hResolved.meta?.width ?? 35}%`, height: `${hResolved.meta?.height ?? 12}%`,
                              background: 'repeating-linear-gradient(45deg, rgba(120,120,140,0.10) 0px, rgba(120,120,140,0.10) 4px, transparent 4px, transparent 10px)',
                              border: '1.5px dashed rgba(100,100,120,0.35)', borderRadius: 1, pointerEvents: 'none',
                            }} />
                          </Box>
                        );
                      }
                      return elem;
                    })}

                    {[...hs.filter(h => h.hotspotType === 'image')].sort((a,b) => (a.order??0)-(b.order??0)).map(h => {
                      const hResolved = resolveHotspotForBp(h, bp);
                      const isVisible = isHotspotVisibleOnBp(h, bp);
                      const elem = (
                        <ImageElement
                          key={h.id}
                          hotspot={hResolved}
                          selected={interactive && selectedId === h.id}
                          previewMode={previewMode || !interactive}
                          canvasScale={canvasScale}
                          onMouseDown={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotMouseDown(e, h.id); } : undefined}
                          onClick={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotClick(h.id); } : undefined}
                          onHandleMouseDown={interactive && !previewMode ? (e, handle) => onHandleMouseDown(e, handle) : undefined}
                          onOpenImagePopover={interactive && !previewMode ? (elemRef) => setImgPopover({ hotspot: h, elemRef }) : undefined}
                          onContextMenu={interactive && !previewMode ? e => openCtxMenu(e, h.id) : undefined}
                        />
                      );
                      if (!previewMode && !isVisible) {
                        return (
                          <Box key={h.id} sx={{ position: 'absolute', inset: 0, pointerEvents: interactive ? 'all' : 'none' }}>
                            <Box sx={{ opacity: 0.25, filter: 'grayscale(0.6)', pointerEvents: 'all' }}>{elem}</Box>
                            <Box sx={{
                              position: 'absolute',
                              left: `${hResolved.points?.[0]?.x ?? 0}%`, top: `${hResolved.points?.[0]?.y ?? 0}%`,
                              width: `${hResolved.meta?.width ?? 25}%`, height: `${hResolved.meta?.height ?? 14}%`,
                              background: 'repeating-linear-gradient(45deg, rgba(120,120,140,0.10) 0px, rgba(120,120,140,0.10) 4px, transparent 4px, transparent 10px)',
                              border: '1.5px dashed rgba(100,100,120,0.35)', borderRadius: 1, pointerEvents: 'none',
                            }} />
                          </Box>
                        );
                      }
                      return elem;
                    })}

                    {interactive && imgPopover && (
                      <ImagePopover
                        hotspot={imgPopover.hotspot}
                        onUpdate={hotspot => { onHotspotUpdate(hotspot); setImgPopover(p => p ? { ...p, hotspot } : null); }}
                        onClose={() => setImgPopover(null)}
                        anchorRef={imgPopover.elemRef}
                        centerAnchor
                      />
                    )}

                    {interactive && ctxMenu && onDeleteHotspot && (
                      <ElementContextMenu
                        x={ctxMenu.x}
                        y={ctxMenu.y}
                        onDelete={() => { onDeleteHotspot(ctxMenu.hotspotId); setCtxMenu(null); }}
                        onClose={() => setCtxMenu(null)}
                      />
                    )}

                    {interactive && canvasSelected && !previewMode && (
                      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10, background: PRIMARY, color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 1, py: 0.4, borderRadius: '4px', pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SettingsOutlined sx={{ fontSize: 11 }} /> Lobby Settings
                      </Box>
                    )}

                    {interactive && isEditingPath && !previewMode && (
                      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10, background: '#1075BA', color: '#fff', fontSize: '0.65rem', fontWeight: 700, px: 1, py: 0.4, borderRadius: '4px', pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        ✏ Editing Path — click point to select &amp; delete, hover path to add, Esc to exit
                      </Box>
                    )}

                    {/* ── Canvas background hit rect — z-index 0, below all hotspots and elements ── */}
                    {interactive && !isPen && !isShapeDraw && !previewMode && (
                      <svg
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'all' }}
                        viewBox={`0 0 ${bpCanvasW} ${bpCanvasH}`}
                        preserveAspectRatio="none"
                      >
                        <CanvasHotspotHitRect
                          selected={selectedId === CANVAS_HOTSPOT.id}
                          onMouseDown={e => onHotspotMouseDown(e, CANVAS_HOTSPOT.id)}
                          onClick={e => { e.stopPropagation(); onHotspotClick(CANVAS_HOTSPOT.id); }}
                        />
                      </svg>
                    )}

                    {/* ── Per-hotspot SVGs — each with its own z-index matching the layer order ── */}
                    {!isPen && !isShapeDraw && [...hs.filter(h => h.hotspotType !== 'stream' && h.hotspotType !== 'livestream-chat' && h.hotspotType !== 'exhibitor-grid' && h.hotspotType !== 'text' && h.hotspotType !== 'image')].sort((a,b) => (a.order ?? 0) - (b.order ?? 0)).map(h => {
                      const hasValidLink = h.hotspotType === 'link' && (
                        (h.hotspotAction?.targetId) ||
                        (h.hotspotAction?.linkTarget === 'external' && h.hotspotAction?.externalUrl)
                      );
                      const isClickableInPreview = previewMode && (hasValidLink || h.hotspotType === 'chat');
                      const hResolved = resolveHotspotForBp(h, bp);
                      const isVisible = previewMode || isHotspotVisibleOnBp(h, bp);
                      return (
                        <svg
                          key={h.id}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 5 + (h.order ?? 0), pointerEvents: 'none', userSelect: 'none', overflow: 'visible', opacity: !previewMode && !isVisible ? 0.3 : 1 }}
                          viewBox={`0 0 ${bpCanvasW} ${bpCanvasH}`}
                          preserveAspectRatio="none"
                        >
                          <g
                            style={{ pointerEvents: interactive ? 'all' : 'none', cursor: interactive && isClickableInPreview ? 'pointer' : 'inherit' }}
                            onMouseDown={interactive && !previewMode ? e => { e.stopPropagation(); onHotspotMouseDown(e, h.id); } : undefined}
                            onClick={interactive ? e => { e.stopPropagation(); onHotspotClick(h.id); } : undefined}
                            onContextMenu={interactive && !previewMode ? e => openCtxMenu(e, h.id) : undefined}
                          >
                            <HotspotSVG hotspot={hResolved} selected={interactive && selectedId === h.id} previewMode={previewMode} canvasW={bpCanvasW} canvasH={bpCanvasH} />
                            {interactive && !previewMode && !isVisible && (
                              <rect x="0" y="0" width={bpCanvasW} height={bpCanvasH} fill="none"
                                stroke="rgba(100,100,120,0.25)" strokeWidth="1" strokeDasharray="6 4" opacity="0.5" />
                            )}
                          </g>
                        </svg>
                      );
                    })}

                    {/* ── Interaction overlay SVG — gizmo + pen/draw + path edit only ── */}
                    <svg
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 9999, pointerEvents: 'none', userSelect: 'none', cursor: interactive && (isPen || isShapeDraw) ? 'crosshair' : 'inherit' }}
                      viewBox={`0 0 ${bpCanvasW} ${bpCanvasH}`}
                      preserveAspectRatio="none"
                    >
                      {/* Full-canvas rect for pen/draw mode — enabled only when drawing */}
                      {interactive && (isPen || isShapeDraw) && (
                        <rect x={0} y={0} width={bpCanvasW} height={bpCanvasH} fill="transparent" stroke="none"
                          style={{ pointerEvents: 'all', cursor: 'crosshair' }}
                          onMouseDown={isShapeDraw ? onDrawMouseDown : undefined}
                          onMouseMove={isShapeDraw ? onDrawMouseMove : undefined}
                          onMouseUp={isShapeDraw ? onDrawMouseUp : undefined}
                        />
                      )}

                      {interactive && (isPen || isShapeDraw) && [...hs.filter(h => h.hotspotType !== 'stream' && h.hotspotType !== 'livestream-chat' && h.hotspotType !== 'exhibitor-grid' && h.hotspotType !== 'text' && h.hotspotType !== 'image')].sort((a,b) => (a.order ?? 0) - (b.order ?? 0)).map(h => (
                        <g key={h.id} style={{ pointerEvents: 'none' }}>
                          <HotspotSVG hotspot={resolveHotspotForBp(h, bp)} selected={false} canvasW={bpCanvasW} canvasH={bpCanvasH} />
                        </g>
                      ))}

                      {interactive && !isPen && !isShapeDraw && !isEditingPath && selectedHotspot && selectedHotspot.hotspotType !== 'livestream-chat' && selectedHotspot.hotspotType !== 'text' && selectedHotspot.hotspotType !== 'image' && (
                        <g style={{ pointerEvents: 'all' }}>
                          <SelectionGizmo
                            hotspot={resolveHotspotForBp(selectedHotspot, bp)}
                            canvasW={bpCanvasW} canvasH={bpCanvasH}
                            onHandleMouseDown={onHandleMouseDown}
                            onBodyMouseDown={onGizmoBodyMouseDown}
                            disableBodyEvents={selectedHotspot.hotspotType === 'exhibitor-grid'}
                          />
                        </g>
                      )}

                      {interactive && isPen && penTool && (
                        <g style={{ pointerEvents: 'all' }}>
                          <PenToolOverlay penTool={penTool} canvasW={bpCanvasW} canvasH={bpCanvasH} />
                        </g>
                      )}

                      {interactive && isEditingPath && pathEdit && selectedHotspot && (
                        <g style={{ pointerEvents: 'all' }}>
                          <PathEditOverlay
                            hotspot={resolveHotspotForBp(selectedHotspot, bp)}
                            pathEdit={pathEdit}
                            canvasW={bpCanvasW} canvasH={bpCanvasH}
                          />
                        </g>
                      )}

                      {interactive && isShapeDraw && <DrawPreview drawState={drawState} activeTool={activeTool} />}
                    </svg>
                  </Box>

                  {/* Carousel slide wizard — shows when this panel's slide has no background */}
                  {panelShowWizard && isCarousel && !previewMode && (
                    <Box sx={{ position: 'absolute', inset: 0, zIndex: 5, borderRadius: 2, overflow: 'hidden', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', pointerEvents: interactive ? 'all' : 'none' }}>
                      {interactive && <LobbyWizard onUpdateLobby={onUpdateLobby} />}
                    </Box>
                  )}
                </Box>
              );

              if (slideTrack) {
                const isRight    = slideTrack.direction === 'right';
                const currentHs  = hotspots;
                const incomingHs = incomingHotspots || [];
                const currentNobg  = !slideTrack.currentBgUrl;
                const incomingNoBg = !slideTrack.incomingBgUrl;
                const leftPanel  = isRight ? renderPanel(currentHs,  true,  currentNobg)  : renderPanel(incomingHs, false, incomingNoBg);
                const rightPanel = isRight ? renderPanel(incomingHs, false, incomingNoBg) : renderPanel(currentHs,  true,  currentNobg);
                return (
                  <Box sx={trackStyle}>
                    {leftPanel}
                    {rightPanel}
                  </Box>
                );
              }
              return renderPanel(hotspots, true, showCarouselWizard);
            })()}

            {ghost && !isPen && !isShapeDraw && (
              <Box sx={{ position: 'absolute', inset: 0, zIndex: 3, border: `2px dashed ${alpha(PRIMARY, 0.5)}`, borderRadius: 2, background: alpha(PRIMARY, 0.04), pointerEvents: 'none' }} />
            )}
          </Box>

          {/* ── Wizard — outside canvas overflow:hidden so clicks work ── */}
          {showWizard && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 20, borderRadius: 2, overflow: 'hidden', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', pointerEvents: 'all' }}>
              <LobbyWizard
                onUpdateLobby={onUpdateLobby}
                onCreateLobby={wizardCreateHandler}
              />
            </Box>
          )}

          {/* ── Carousel: header rendered outside canvasRef so it sits above the bg-missing state ── */}
          {isCarousel && lobbyHeaderConfig && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 25, pointerEvents: 'all' }}>
              <LobbyHeader
                config={lobbyHeaderConfig}
                selected={headerSelected}
                onSelect={onSelectHeader}
                onChangeLinks={onChangeLinks}
                onChangeConfig={onChangeConfig}
                lobbies={lobbies}
                isBoothLobby={false}
                activeLobby={lobby}
                onChangeLobby={onUpdateLobby}
                isMobile={bp === 'mobile'}
                defaultLogoSrc={defaultLogoSrc}
                onBackToFirstSlide={activeSlideIndex > 0 ? onBackToFirstSlide : undefined}
              />
            </Box>
          )}

          {/* ── Caller-supplied overlay (e.g. carousel nav arrows + dots) — zIndex above wizard ── */}
          {overlayContent}

        </Box>
      </Box>
    </Box>
  );
}